import type { ComponentChildren, ComponentType } from 'preact'
import { useState } from 'preact/hooks'
import { AlertTriangle } from 'lucide-preact'
import { GoogleDrivePicker } from '@/components/common/GoogleDrivePicker'
import { OneDrivePicker } from '@/components/common/OneDrivePicker'
import { DropboxChooser } from '@/components/common/DropboxChooser'
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon'
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon'
import { DropboxIcon } from '@/components/common/icons/DropboxIcon'
import { useGoogleDriveAuth } from '@/hooks/useGoogleDriveAuth'
import { useOneDriveAuth } from '@/hooks/useOneDriveAuth'
import { useDropboxAuth } from '@/hooks/useDropboxAuth'
import { isCloudFileOverLimit, cloudFileSizeLabel, cloudMaxSizeLabel } from '@/components/webs/modals/cloudUploadSize'
import { CLOUD_PROVIDERS, CLOUD_PROVIDER_LABEL, fromDropbox, fromGoogleDrive, fromOneDrive, type CloudProvider, type PickedCloudFile } from './cloudFile'

const PROVIDER_ICON: Record<CloudProvider, ComponentType<{ className?: string }>> = {
    GoogleDrive: GoogleDriveIcon,
    OneDrive: OneDriveIcon,
    Dropbox: DropboxIcon,
}

interface CloudFilePickerProps {
    /** Checks each provider's connection while true; a picker cannot open until it knows. */
    enabled: boolean
    /** The provider shown, or null while the caller's leading tab is. */
    provider: CloudProvider | null
    onProviderChange: (provider: CloudProvider | null) => void
    /** The largest file the caller's API accepts, or null while it is not known. Served by the API, never a constant. */
    maxBytes: number | null
    /** A file within the limit as it stood when picked, or null once it is cleared, too large, or the provider changes. A caller whose limit can arrive later judges the file again. */
    onPicked: (file: PickedCloudFile | null) => void
    /** The caller's own source, shown as the first tab. Its body is the caller's to render while it is selected. */
    leadingTab?: { label: string; icon: ComponentChildren }
}

function tabClass(selected: boolean): string {
    return `flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
        selected
            ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
            : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`
}

/**
 * Google Drive, OneDrive and Dropbox behind one control (ORB-1086): the provider tabs, each provider's own picker
 * and sign-in, and the size check against the caller's ceiling. Every pick comes out as one `PickedCloudFile`.
 */
export function CloudFilePicker({ enabled, provider, onProviderChange, maxBytes, onPicked, leadingTab }: CloudFilePickerProps) {
    const googleDriveAuth = useGoogleDriveAuth(enabled)
    const oneDriveAuth = useOneDriveAuth(enabled)
    const dropboxAuth = useDropboxAuth(enabled)
    const [picked, setPicked] = useState<PickedCloudFile | null>(null)
    const [error, setError] = useState<string | undefined>()

    const choose = (next: CloudProvider | null) => {
        if (next === provider) return
        setPicked(null)
        setError(undefined)
        onPicked(null)
        onProviderChange(next)
    }

    const take = (file: PickedCloudFile) => {
        setPicked(file)
        setError(undefined)
        onPicked(isCloudFileOverLimit(file.sizeBytes, maxBytes) ? null : file)
    }

    const tooLarge = !!picked && isCloudFileOverLimit(picked.sizeBytes, maxBytes)
    const sizeText = picked ? cloudFileSizeLabel(picked.sizeBytes) : null
    const maxText = cloudMaxSizeLabel(maxBytes)

    return (
        <div className="space-y-6">
            <div className="flex gap-2">
                {leadingTab && (
                    <button type="button" onClick={() => choose(null)} className={tabClass(provider === null)} aria-pressed={provider === null}>
                        {leadingTab.icon}
                        {leadingTab.label}
                    </button>
                )}
                {CLOUD_PROVIDERS.map(key => {
                    const Icon = PROVIDER_ICON[key]
                    return (
                        <button key={key} type="button" onClick={() => choose(key)} className={tabClass(provider === key)} aria-pressed={provider === key}>
                            <Icon className="w-4 h-4" />
                            {CLOUD_PROVIDER_LABEL[key]}
                        </button>
                    )
                })}
            </div>

            {provider === 'GoogleDrive' && <GoogleDrivePicker auth={googleDriveAuth} onFilePicked={file => take(fromGoogleDrive(file))} onError={setError} />}
            {provider === 'OneDrive' && <OneDrivePicker auth={oneDriveAuth} onFilePicked={file => take(fromOneDrive(file))} onError={setError} />}
            {provider === 'Dropbox' && <DropboxChooser auth={dropboxAuth} onFilePicked={file => take(fromDropbox(file))} onError={setError} />}
            {provider && error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            {provider && picked && (
                <div className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        {sizeText ? `Selected file size: ${sizeText}` : 'Selected file size: not reported by this provider'}
                        {maxText && ` · Maximum: ${maxText}`}
                    </p>
                    {tooLarge && (
                        <div className="flex items-start gap-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700 dark:text-red-300">
                                This file is {sizeText}, which is over the {maxText} maximum. Please choose a smaller file.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
